import { motion, type HTMLMotionProps } from "framer-motion";
import { type ReactNode } from "react";

type Props = {
  title: string;
  url?: string;
  status?: string;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  motionProps?: HTMLMotionProps<"div">;
};

/**
 * Win98/Netscape-flavored window chrome. Used for content panels and as a
 * frame around live iframes.
 */
export function RetroWindow({
  title,
  url,
  status,
  children,
  className = "",
  bodyClassName = "",
  motionProps,
}: Props) {
  return (
    <motion.div
      {...motionProps}
      className={`bevel-out bg-grimoire ${className}`}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-bruise via-witch to-orchid px-2 py-1.5 border-b-2 border-black">
        <div className="flex items-center gap-2 min-w-0">
          <CauldronGlyph className="w-4 h-4 shrink-0" />
          <span className="font-display text-[10px] tracking-tight text-bone truncate">
            {title}
          </span>
        </div>
        <div className="flex gap-1">
          <WindowButton glyph="_" />
          <WindowButton glyph="□" />
          <WindowButton glyph="×" />
        </div>
      </div>

      {/* Optional URL bar */}
      {url && (
        <div className="flex items-center gap-2 bg-night px-2 py-1.5 border-b-2 border-black">
          <span className="font-terminal text-mystic text-xs">URL</span>
          <div className="bevel-in flex-1 bg-void px-2 py-0.5">
            <span className="font-terminal text-aura text-xs truncate block">
              {url}
            </span>
          </div>
          <span className="font-display text-[8px] text-acid animate-pulse">● LIVE</span>
        </div>
      )}

      {/* Body */}
      <div className={bodyClassName}>{children}</div>

      {/* Optional status bar */}
      {status && (
        <div className="bevel-in bg-night px-2 py-0.5 border-t-2 border-black">
          <span className="font-terminal text-aura text-xs">{status}</span>
        </div>
      )}
    </motion.div>
  );
}

function WindowButton({ glyph }: { glyph: string }) {
  return (
    <button
      type="button"
      className="bevel-out w-5 h-5 bg-aura flex items-center justify-center text-black font-display text-[8px] leading-none active:bevel-in"
      tabIndex={-1}
      aria-hidden
    >
      {glyph}
    </button>
  );
}

function CauldronGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className} shapeRendering="crispEdges">
      <rect x="2" y="6" width="12" height="2" fill="#1a0028" />
      <rect x="1" y="8" width="14" height="5" fill="#06000d" />
      <rect x="3" y="6" width="10" height="1" fill="#9b3df5" />
      <rect x="4" y="13" width="8" height="1" fill="#06000d" />
      <rect x="5" y="14" width="6" height="1" fill="#06000d" />
    </svg>
  );
}
